import React, { useCallback } from 'react';
import { View } from 'common/types/view';
import { RelatedStories } from 'types';
import { ForgeAutocomplete, ForgeIcon, ForgeTextField } from '@tylertech/forge-react';
import optionallyLocalizeUrls from 'common/site_chrome/app/assets/javascripts/socrata_site_chrome/utils/optionally_localize_urls';
import { I18n } from 'common/i18n';
import { useDispatch } from 'react-redux';
import { setPageLoading } from 'store/LoadingReduxState';
import { useCache } from 'util/use-cache';

export function DerivedReportSelector({
  view,
  relatedStories,
  open = false,
}: {
  view: View;
  relatedStories: RelatedStories;
  open?: boolean; // used for testing
}) {
  const dispatch = useDispatch();

  const filter = useCallback(
    (filterText: string) => {
      filterText = filterText.toLowerCase();
      const containsFilter = (v: View) => v.name.toLowerCase().includes(filterText);

      const { defaultStory, derivedStories } = relatedStories;
      const relatedOptionGroup = {
        text: I18n.t('derived_story.derived_list_title'),
        options: derivedStories.filter(containsFilter).map((v) => ({
          label: v.name,
          value: v.id,
          secondaryLabel: v.description,
        })),
      };

      let defaultOptionGroup;
      if (!defaultStory || !containsFilter(defaultStory)) {
        defaultOptionGroup = { options: [] };
      } else {
        defaultOptionGroup = {
          options: [
            {
              label: `${defaultStory.name} ${I18n.t('derived_story.default_marker')}`,
              value: defaultStory.id,
              secondaryLabel: defaultStory.description,
            },
            { label: '', value: null, divider: true },
          ],
        };
      }
      return [defaultOptionGroup, relatedOptionGroup];
    },
    [relatedStories],
  );

  const { wrapped: getOptionsWrapper } = useCache(filter);

  const onChange = (e: CustomEvent<string | null>) => {
    const reportId = e.detail;

    if (reportId && reportId !== view.id) {
      dispatch(setPageLoading({ loading: true }));
      window.open(optionallyLocalizeUrls(`/d/${reportId}`), '_self');
    }
  };

  return (
    <ForgeAutocomplete
      on-forge-autocomplete-change={onChange}
      allowUnmatched={false}
      data-testid="derived-report-autocomplete"
      filter={getOptionsWrapper}
      value={view.id}
      open={open}
    >
      <ForgeTextField density={'small'}>
        <input aria-label={I18n.t('derived_story.select_a_report')} type="text" id="picker-input" />
        <ForgeIcon slot="end" data-forge-dropdown-icon name="arrow_drop_down" />
      </ForgeTextField>
    </ForgeAutocomplete>
  );
}
